import { NavigateFunction } from 'react-router-dom';

/**
 * Imperative navigation singleton for use outside of React components.
 *
 * ## Why this exists
 *
 * React Router's `useNavigate` is a hook — it can only be called inside a React
 * component or custom hook. However, some navigation needs to happen in places
 * that have no access to the React tree, most notably axios interceptors.
 *
 * A concrete example: when the backend returns error code 1100
 * (ACCESS_ERROR_NO_ACTIVE_SUBSCRIPTIONS_FOUND), the axios response interceptor
 * in `axios.ts` must redirect the user to `/billing/expired`. At that point,
 * we're in plain module-level code — no component, no hook, no context.
 *
 * The naive fix (`window.location.replace`) causes a full page reload, which
 * produces a visible flash: React renders the correct page for a frame, then
 * the browser reloads from scratch. Using `navigate()` from React Router keeps
 * everything client-side and flicker-free.
 *
 * ## How it works
 *
 * 1. `AppRoutes` (inside `BrowserRouter`) calls `setNavigate(navigate)` once on
 *    mount via `useEffect`, storing the live `NavigateFunction` here.
 * 2. Any non-React code calls `navigateTo(path)`, which delegates to the stored
 *    function — or falls back to `window.location.replace` if called before the
 *    router has mounted (edge case that should never happen in practice).
 *
 * ## What NOT to use this for
 *
 * Do not use this inside React components or hooks — use `useNavigate` directly
 * there. This singleton exists solely to bridge the gap between the React Router
 * context and code that lives outside it.
 */
let _navigate: NavigateFunction | null = null;

export const setNavigate = (navigate: NavigateFunction): void => {
  _navigate = navigate;
};

export const navigateTo = (path: string): void => {
  if (_navigate) {
    _navigate(path, { replace: true });
  } else {
    // Fallback for the rare case where this is called before the router mounts
    window.location.replace(path);
  }
};
